import { appConstants } from "../../../redux/action-constants/inapp.constants";
import { ImageType, MediaPageProps } from "../../../types/app/mediaTypes";
import { ImageCard } from "./ImageCard";
import "./Image.scss";
import { LoadingItem } from "../../../shared/components/loading";
import { ActionProps } from "./InAppContent";
import { useState } from "react";
import UploadImageModal from "./UploadImageModal";

type ImageData = {
    name: string;
    categoryName: string;
    file: File;
    otherNames: string[];
};
interface Props {
    pageProps: MediaPageProps;
    toggleSelect: (index: number) => void;
    handleEdit: (index: number) => void;
    selected: number[];
    onLoadMore: () => void;
    isNewBatchLoading: boolean;
    onSearch: (value: string) => void;
    isSearchLoading: boolean;
    setSearchValue: (searchValue: string) => void;
    searchValue: string;
    onClearSelection: () => void;
    onSelectAll: () => void;
    setSortOption: (sortOption: string) => void;
    sortOption: string;
    setLoadimgs: (loadImgs: number) => void;
    loadImgs: number;
    sortedGalleryData: ImageType[];
    handleDeleteImages: (imageFilenames: string[]) => Promise<{
        isSuccess: boolean;
        isError: boolean;
    }>;
    handleUploadImages: (images: ImageData[], onProgress?: (progress: number) => void) => Promise<{
        isSuccess: boolean;
        isError: boolean;
    }>;
    handleResetUploadProductImage: () => void;
    getProductCategoriesAction: ({ pageProps, payload }: ActionProps) => Promise<void>;
}

const ReportDisplay = ({
    pageProps,
    toggleSelect,
    handleEdit,
    selected,
    onLoadMore,
    isNewBatchLoading,
    onSearch,
    isSearchLoading,
    searchValue,
    setSearchValue,
    onClearSelection,
    onSelectAll,
    setSortOption,
    sortedGalleryData,
    sortOption,
    loadImgs,
    setLoadimgs,
    handleDeleteImages,
    handleUploadImages,
    handleResetUploadProductImage,
    getProductCategoriesAction
}: Props) => {
    const [openUploadImageModal, setOpenUploadImageModal] = useState<boolean>(false);

    const handleDeleteSelectedImages = async () => {
        const imagesToDelete = selected
            .filter((i) => i >= 0 && i < sortedGalleryData.length) // ensure valid indexes
            .map((i) => sortedGalleryData[i]?.name);

        await handleDeleteImages(imagesToDelete as string[])
        if (pageProps.deleteImageRequest.request_status === appConstants.DELETE_IMAGE_SUCCESS) {
            onClearSelection()
        }
    };
    return (
        <div className="media-wrapper">
            <div className="media-header">
                <div className="top-row">
                    <h2>
                        Media Library{" "}
                        <span className="count-badge">
                            {
                                pageProps?.getImageGalleryRequest?.request_data?.response?.totalNumberOfRecords
                                    ? pageProps?.getImageGalleryRequest?.request_data?.response?.totalNumberOfRecords
                                    : '...'
                            }

                        </span>
                    </h2>
                    <button className="add-media-btn" onClick={() => {
                        setOpenUploadImageModal(true)
                    }}>
                        <svg width="16" height="16" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M19 11H13V5h-2v6H5v2h6v6h2v-6h6z" />
                        </svg>
                        Add New Media
                    </button>
                </div>
                {selected.length > 0 ? (
                    <div className="bulk-bar">
                        <div className="bulk-left">
                            <button className="close-bulk" onClick={onClearSelection}>
                                &times;
                            </button>
                            <span className="selected-count">{selected.length} item{selected.length > 1 && 's'} selected</span>
                            <span className="select-all" onClick={onSelectAll}>Select all items</span>
                        </div>

                        <div className="bulk-actions">
                            <button className="bulk-icon-btn" >
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="black" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                                    <polyline points="7 10 12 15 17 10" />
                                    <line x1="12" y1="15" x2="12" y2="3" />
                                </svg>
                            </button>

                            <button className="bulk-icon-btn" disabled>
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="black" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M12 20h9" />
                                    <path d="M16.5 3.5a2.121 2.121 0 1 1 3 3L7 19l-4 1 1-4 12.5-12.5z" />
                                </svg>
                            </button>

                            <button className="bulk-icon-btn" onClick={() => handleDeleteSelectedImages()} disabled={pageProps?.deleteImageRequest?.is_request_processing}>
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="black" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <polyline points="3 6 5 6 21 6" />
                                    <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6m5 0V4a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1v2" />
                                </svg>
                            </button>
                        </div>
                    </div>
                ) : (

                    <div className="bottom-row">
                        <div className="bottom-left">
                            <div className="search-box">
                                <svg className="search-icon" viewBox="0 0 24 24" fill="none" stroke="black"
                                    strokeWidth="1"
                                    strokeLinecap="round"
                                    strokeLinejoin="round">
                                    <path
                                        d="M11 4a7 7 0 015.66 11.31l4.02 4.02-1.41 1.41-4.02-4.02A7 7 0 1111 4zm0 2a5 5 0 100 10 5 5 0 000-10z"
                                        fill="#888"
                                    />
                                </svg>
                                <input type="text" placeholder="Type to search for an image"
                                    value={searchValue}
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                        setSearchValue(e.target.value)
                                        if (e.target.value === "") {
                                            onSearch("")
                                        }
                                    }}
                                    onKeyDown={(e: any) => {
                                        if (e.key === 'Enter') {
                                            onSearch(e.target.value.trim())
                                        }
                                    }} />
                            </div>

                            <button className="filter-btn">
                                Filter

                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="24"
                                    height="24"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="black"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                >
                                    <line x1="4" y1="6" x2="20" y2="6"></line>
                                    <circle
                                        cx="12"
                                        cy="6"
                                        r="2"
                                        fill="white"
                                        stroke="black"
                                    ></circle>
                                    <line x1="4" y1="12" x2="20" y2="12"></line>
                                    <circle
                                        cx="8"
                                        cy="12"
                                        r="2"
                                        fill="white"
                                        stroke="black"
                                    ></circle>
                                    <line x1="4" y1="18" x2="20" y2="18"></line>
                                    <circle
                                        cx="16"
                                        cy="18"
                                        r="2"
                                        fill="white"
                                        stroke="black"
                                    ></circle>
                                </svg>
                            </button>
                        </div>

                        <select
                            className="sort-dropdown"
                            value={sortOption}
                            onChange={(e) => setSortOption(e.target.value)}
                        >
                            <option value="new-to-old">Date added - new to old</option>
                            <option value="old-to-new">Date added - old to new</option>
                            <option value="a-to-z">Title - A to Z</option>
                            <option value="z-to-a">Title - Z to A</option>
                            <option value="large-to-small">File size - large to small</option>
                            <option value="small-to-large">File size - small to large</option>
                        </select>
                    </div>
                )}
            </div>
            <div className="image-grid">
                {
                    (
                        pageProps.getImageGalleryRequest.request_status === appConstants.GET_IMAGE_GALLERY_SUCCESS ||
                        pageProps.getImageGalleryRequest.request_status === appConstants.GET_IMAGE_GALLERY_NEWBATCH
                    ) && (
                        sortedGalleryData.length ? (
                            sortedGalleryData.map((img, idx) => (
                                <ImageCard
                                    key={`${idx}-${loadImgs}`} // key changes when loadImgs changes
                                    image={img}
                                    isSelected={selected.includes(idx)}
                                    onSelect={() => toggleSelect(idx)}
                                    onEdit={() => handleEdit(idx)}
                                />
                            ))
                        )
                            : <div className="empty-gallery">No media data</div>
                    )
                }

            </div>
            {isNewBatchLoading &&
                <LoadingItem />
            }
            {isSearchLoading &&
                <LoadingItem />
            }
            {pageProps?.getImageGalleryRequest?.galleryData?.length !== pageProps?.getImageGalleryRequest?.request_data?.response?.totalNumberOfRecords &&
                <div className="bottom-fade-overlay">
                    <button className="center-fade-btn" onClick={onLoadMore} disabled={isNewBatchLoading}>
                        Load 100 more images
                    </button>
                </div>
            }
            {openUploadImageModal &&
                <UploadImageModal show={openUploadImageModal}
                    getProductCategoriesAction={getProductCategoriesAction}
                    handleResetUploadProductImage={handleResetUploadProductImage} pageProps={pageProps} handleUploadImages={handleUploadImages} onClose={() => setOpenUploadImageModal(false)} />
            }
        </div>
    );
};

export default ReportDisplay;
